import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

export default function Blob3D() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let W = mount.clientWidth || 460;
    let H = mount.clientHeight || 460;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(38, W / H, 0.1, 100);
    const cameraZ = (h) => h < 300 ? 4.3 * (300 / h) : 4.3;
    camera.position.set(0, 0, cameraZ(H));

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.35));

    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(4, 6, 5);
    scene.add(key);

    const purple = new THREE.PointLight(0x9477ff, 7.0, 30);
    purple.position.set(-5, 2, 4);
    scene.add(purple);

    const deep = new THREE.PointLight(0x5a3acd, 5.0, 30);
    deep.position.set(3, -4, -5);
    scene.add(deep);

    const top = new THREE.PointLight(0xffffff, 4.5, 30);
    top.position.set(0, 7, 2);
    scene.add(top);

    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();

    const envScene = new THREE.Scene();
    envScene.add(new THREE.Mesh(
      new THREE.SphereGeometry(50, 16, 16),
      new THREE.MeshBasicMaterial({ side: THREE.BackSide, color: 0x2a2444 })
    ));

    const panels = [
      { p: [  10,   8,   4], c: 0xffffff, r: 6 },
      { p: [  -9,   5,   7], c: 0xd6c2ff, r: 5 },
      { p: [   6, -10,  -3], c: 0xb39dff, r: 4 },
      { p: [ -11,  -4,  -8], c: 0x7253e0, r: 5 },
      { p: [   0,  12,  -5], c: 0xffffff, r: 5 },
      { p: [   8,   2, -10], c: 0x5a3acd, r: 5 },
      { p: [  -3,  -8,   9], c: 0xa794ff, r: 4 },
      { p: [  -8,  10,  -2], c: 0xeae3ff, r: 3 },
    ];
    panels.forEach(({ p, c, r }) => {
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(r, 20, 20),
        new THREE.MeshBasicMaterial({ color: c })
      );
      m.position.set(p[0], p[1], p[2]);
      envScene.add(m);
    });

    let envMap = null;
    try {
      envMap = pmrem.fromScene(envScene, 0.04).texture;
      scene.environment = envMap;
    } catch (e) {}

    function makeSmoothIcoGeom(radius, detail) {
      const raw = new THREE.IcosahedronGeometry(radius, detail);
      const src = raw.attributes.position.array;
      const N = raw.attributes.position.count;
      const map = new Map();
      const verts = [];
      const indices = [];
      for (let i = 0; i < N; i++) {
        const x = src[i * 3];
        const y = src[i * 3 + 1];
        const z = src[i * 3 + 2];
        const k = `${x.toFixed(5)}|${y.toFixed(5)}|${z.toFixed(5)}`;
        let idx = map.get(k);
        if (idx === undefined) {
          idx = verts.length / 3;
          verts.push(x, y, z);
          map.set(k, idx);
        }
        indices.push(idx);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
      g.setIndex(indices);
      g.computeVertexNormals();
      raw.dispose();
      return g;
    }

    const geom = makeSmoothIcoGeom(1.3, 6);
    const orig = new Float32Array(geom.attributes.position.array);

    const mat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 1.0,
      roughness: 0.18,
      envMapIntensity: 1.6,
    });
    const blob = new THREE.Mesh(geom, mat);
    blob.scale.set(1.55, 0.9, 1.0);
    scene.add(blob);

    function n3(x, y, z) {
      return (
        Math.sin(x * 1.7 + y * 2.3 + z * 1.9) * 0.45 +
        Math.sin(x * 3.1 + y * 1.5 + z * 2.7 + 1.0) * 0.30 +
        Math.sin(x * 0.7 + y * 5.1 + z * 1.3 + 2.0) * 0.22 +
        Math.sin(x * 4.3 + y * 0.9 + z * 3.5 + 3.0) * 0.13
      );
    }

    const clock = new THREE.Clock();
    let raf = 0;
    let lastFrame = 0;

    let mouseX = 0, mouseY = 0;
    let targetX = 0, targetY = 0;
    let isDown = false;
    let pinchAmt = 0;
    let pinchDirX = 0, pinchDirY = 0;

    function setPointer(clientX, clientY) {
      mouseX = (clientX / window.innerWidth  - 0.5) * 2;
      mouseY = (clientY / window.innerHeight - 0.5) * 2;
    }
    function startPress(clientX, clientY) {
      isDown = true;
      pinchDirX = (clientX / window.innerWidth  - 0.5) * 2;
      pinchDirY = -((clientY / window.innerHeight - 0.5) * 2);
    }

    function onMouseMove(e) { setPointer(e.clientX, e.clientY); }
    function onMouseDown(e) { startPress(e.clientX, e.clientY); }
    function onMouseUp()    { isDown = false; }

    function onTouchMove(e)  { const t = e.touches[0]; setPointer(t.clientX, t.clientY); }
    function onTouchStart(e) { const t = e.touches[0]; startPress(t.clientX, t.clientY); }
    function onTouchEnd()    { isDown = false; }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup',   onMouseUp);
    window.addEventListener('touchmove',  onTouchMove,  { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend',   onTouchEnd);

    function renderFrame() {
      const t = clock.getElapsedTime() * 0.42;
      const pos = geom.attributes.position;
      const arr = pos.array;
      const tx = t * 0.5, ty = t * 0.35, tz = t * 0.45;

      pinchAmt += ((isDown ? 1 : 0) - pinchAmt) * (isDown ? 0.07 : 0.02);

      for (let i = 0; i < pos.count; i++) {
        const ix = i * 3;
        const ox = orig[ix], oy = orig[ix + 1], oz = orig[ix + 2];
        const n1 = n3(ox * 1.0 + tx, oy * 1.0 + ty, oz * 1.0 + tz);
        const n2 = n3(ox * 2.2 - ty, oy * 2.2 + tx, oz * 2.2 - tz);
        const d = 1 + n1 * 0.16 + n2 * 0.05;
        let px = ox * d, py = oy * d, pz = oz * d;
        if (pinchAmt > 0.001) {
          const pull = (px * pinchDirX + py * pinchDirY) / 1.5;
          px += pinchDirX * pinchAmt * 0.4 * pull;
          py += pinchDirY * pinchAmt * 0.4 * pull;
          pz *= 1 - pinchAmt * 0.15;
        }
        arr[ix]     = px;
        arr[ix + 1] = py;
        arr[ix + 2] = pz;
      }
      pos.needsUpdate = true;
      geom.computeVertexNormals();

      targetX += (mouseX - targetX) * 0.04;
      targetY += (mouseY - targetY) * 0.04;

      blob.rotation.y = t * 0.18 + targetX * 0.4;
      blob.rotation.x = Math.sin(t * 0.3) * 0.18 - targetY * 0.25;
      blob.scale.set(1.55, 0.9, 1.0);

      renderer.render(scene, camera);
      lastFrame = performance.now();
    }

    function loop() {
      raf = requestAnimationFrame(loop);
      renderFrame();
    }
    loop();

    const fallback = setInterval(() => {
      if (performance.now() - lastFrame > 1000) renderFrame();
    }, 1500);

    const ro = new ResizeObserver(() => {
      W = mount.clientWidth;
      H = mount.clientHeight;
      if (W && H) {
        renderer.setSize(W, H);
        camera.aspect = W / H;
        camera.position.z = cameraZ(H);
        camera.updateProjectionMatrix();
        renderFrame();
      }
    });
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf);
      clearInterval(fallback);
      ro.disconnect();
      window.removeEventListener('mousemove',  onMouseMove);
      window.removeEventListener('mousedown',  onMouseDown);
      window.removeEventListener('mouseup',    onMouseUp);
      window.removeEventListener('touchmove',  onTouchMove);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend',   onTouchEnd);
      pmrem.dispose();
      if (envMap) envMap.dispose();
      geom.dispose();
      mat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className="blob3d" aria-hidden="true" />;
}
